import { getCookie } from "cookies-next";
import { format, isValid, parseISO } from "date-fns";
import filter from "lodash/filter";
import find from "lodash/find";
import forEach from "lodash/forEach";
import get from "lodash/get";
import map from "lodash/map";
import pick from "lodash/pick";
import sum from "lodash/sum";

import {
  IGuestRequestQuoteChild,
  IGuestRequestQuoteMaster,
  IProject,
  IReview,
} from "~/types/api-types";
import {
  AiAnswersEventProperties,
  FeeObject,
  SegmentedListingObject,
  PartyCalculations,
  BookingAddOnItem,
  NormalizedContractObject,
  NormalizedBoardObject,
  ParseFeeObjParams,
  SegmentedListingProps,
} from "~/types/local-types";
import { SSO_ID_COOKIE } from "~/utils/constants";

export const segmentListingObj = (
  listing: SegmentedListingProps,
  reviews: IReview[],
) => {
  const hasCleaningFee = listing.cleaningFeeSet;
  const hasDayRate = listing.dayRateDiscountSet;
  const segmentListingObj: SegmentedListingObject = {
    listing_id: listing.listingId,
    metro: listing.metro,
    neighborhood: listing.neighborhood,
    city: listing.city,
    state: listing.state,
    zip: listing.zip,
    country: listing.country,
    use_type: listing.useType,
    total_reviews: listing.spaceReviewCount,
    space_review_count: listing.spaceReviewCount,
    space_review_stars: listing.spaceReviewStars,
    title: listing.title,
    cover_img_url: listing.coverImgUrl,
    currency_type: listing.currencyType,
    currency_symbol: listing.currencySymbol,
    ownerAvatarUrl: listing.ownerAvatarUrl,
    ownerId: listing.ownerId,
    ownerFirstName: listing.ownerFirstName,
    ownerLastName: listing.ownerLastName,
    price_hourly: listing.priceHourly,
    cleaning_fee_set: hasCleaningFee,
    cleaning_fee_amount: hasCleaningFee ? listing.cleaningFeeAmount : 0,
    day_rate_discount_set: hasDayRate,
    day_rate_discount_percent: hasDayRate ? listing.dayRateDiscountPercent : 0,
    next_page: reviews ? reviews.length : null,
  };
  if (listing.hostResponseBucket) {
    segmentListingObj.host_response_time = listing.hostResponseTime;
    segmentListingObj.host_response_bucket = listing.hostResponseBucket;
    segmentListingObj.host_response_status = listing.hostResponseStatus;
  }
  return segmentListingObj;
};

export const parseFeeObj = (feeObj: ParseFeeObjParams) => {
  const keyArray = [
    "guest_payment_processing_fee_percent",
    "guest_payment_processing_fee",
    "host_warnings",
    "guest_payment_amount",
    "subtotal_amount",
    "guest_id",
    "host_id",
    "duration",
    "discounts",
  ];
  // Values picked from listing obj
  const editedFeeObj: Partial<FeeObject> = pick(feeObj, keyArray);
  const filteredDiscounts = Array.isArray(editedFeeObj.discounts)
    ? editedFeeObj.discounts.filter((o) => {
        return "DAY_RATE_DISCOUNT" === o.type;
      })
    : [];
  const hasDayRate = filteredDiscounts.length;
  editedFeeObj.day_rate_discount_amount = hasDayRate
    ? filteredDiscounts[0].discount_amount
    : "";
  editedFeeObj.display_date = feeObj.start.display.date;
  editedFeeObj.display_start = feeObj.start.display.time;
  editedFeeObj.display_end = feeObj.end.display.time;
  return editedFeeObj;
};

export const normalizeContractObj = (
  contract: [IGuestRequestQuoteMaster, IGuestRequestQuoteChild],
  action: string,
  publicAddOns?: BookingAddOnItem[],
) => {
  const masterContract =
    (find(contract, { type: "MASTER" }) as IGuestRequestQuoteMaster) || {};
  const spaceRentalContract =
    (find(contract, { type: "SPACE_RENTAL" }) as IGuestRequestQuoteChild) || {};
  const pendingContract =
    "HOST_UPDATE_SUBMITTED" === masterContract.state ||
    "OFFER_SUBMITTED" === masterContract.state ||
    "PENDING_GUEST_REQUEST_ACCEPT" === masterContract.state;
  const itemsArray = filter(contract, { type: "ITEM" });
  const hostId = masterContract.host_sso_id;
  const bookingObj = get(spaceRentalContract, "meta.booking");
  const { calculations = { guest: {}, host: {} } } = masterContract;
  const guestAmounts = (calculations?.guest || {}) as PartyCalculations;
  const hostAmounts = (calculations?.host || {}) as PartyCalculations;
  const booking_status = get(masterContract, "state");

  const guest_payment_amount = guestAmounts.payment || 0;
  const guest_payment_processing_fee = guestAmounts.fee || 0;
  const guest_payment_processing_fee_percent = guestAmounts.fee_percent || 0;
  const host_payout_amount = hostAmounts.payout || 0;
  const host_payout_processing_fee = hostAmounts.fee || 0;
  const host_payout_processing_fee_percent = hostAmounts.fee_percent || 0;
  const subtotal_amount =
    Number(get(masterContract, `amounts.amount[${hostId}]`)) || 0;
  // add-on amounts
  const host_add_ons_guest_processing_fee = get(
    calculations,
    "tenant.breakouts.add_ons.guest.broker_fee",
    0,
  );
  const host_add_ons_host_processing_fee = get(
    calculations,
    "tenant.breakouts.add_ons.host.broker_fee",
    0,
  );
  const host_add_ons_host_payout = get(
    calculations,
    "tenant.breakouts.add_ons.host.payout",
    0,
  );
  const host_add_ons_base = get(calculations, "add_ons.unit_cost", 0);
  const host_add_ons_taxes = get(calculations, "add_ons.taxes", 0);
  const host_add_ons_fees = get(calculations, "add_ons.fees", 0);
  const host_add_ons_subtotal = get(calculations, "add_ons.amount", 0);
  // space rental amounts
  const space_rental_guest_processing_fee = get(
    calculations,
    "tenant.breakouts.space_rentals.guest.broker_fee",
    0,
  );
  const space_rental_host_processing_fee = get(
    calculations,
    "tenant.breakouts.space_rentals.host.broker_fee",
    0,
  );
  const space_rental_processing_fee =
    space_rental_guest_processing_fee + space_rental_host_processing_fee;
  const space_rental_host_payout = get(
    calculations,
    "tenant.breakouts.space_rentals.host.payout",
    0,
  );
  // cleaning fee
  const addtlCharges = get(bookingObj, "addtl_charges", []);
  const cleaningFee = find(addtlCharges, function (charge: { type: string }) {
    return "CLEANING_FEE" === charge.type;
  });
  const cleaning_fee_set = !!cleaningFee;
  const cleaning_fee_amount = get(cleaningFee, "value");
  const dayRateDiscount = bookingObj.discounts?.find(
    (d) => d.type === "DAY_RATE_DISCOUNT",
  );
  const day_rate_set = !!dayRateDiscount;
  const day_rate_amount = get(dayRateDiscount, "discount_amount");
  const day_rate_percent = get(dayRateDiscount, "value");
  // variable pricing
  const bkg_has_price_by_day = get(
    bookingObj,
    "rate_details.is_rate_variable",
    false,
  ) as boolean;
  const bkg_has_attendee_rate = !!get(
    bookingObj,
    "rate_details.attendee_pricing.upcharge",
    false,
  );
  const attendee_upcharge = get(
    bookingObj,
    "rate_details.attendee_pricing.upcharge",
    0,
  );
  const hourly_rate = get(bookingObj, "rate", 0);
  // duration
  const day_count = get(bookingObj, "daily_durations", []).length;
  // add-ons details
  const addOnsArray: BookingAddOnItem[] = [];
  forEach(itemsArray, (item) => {
    const bookingItem = item as unknown as BookingAddOnItem;
    const { meta, secondary_charges } = bookingItem;
    const taxesArray: {
      amount: number;
      description: string;
      meta: { type: string; charge_type: string; amount: number };
    }[] = filter(secondary_charges, { meta: { charge_type: "tax" } });
    const taxTotal = sum(
      map(taxesArray, function (tax) {
        return tax.amount;
      }),
    ).toFixed(2);
    const taxTotalPercent = sum(
      map(taxesArray, function (tax) {
        return get(tax, "meta.amount", 0);
      }),
    ).toFixed(4);
    const feesArray = filter(secondary_charges, {
      meta: { charge_type: "fee" },
    });
    const feeTotal = sum(
      map(feesArray, function (fee) {
        return fee.amount;
      }),
    ).toFixed(2);
    const imageId = get(item, "meta.image_ids[0]", "");
    const itemObj = {
      item_id: bookingItem._id,
      category: meta.category,
      subcategory: meta.subcategory,
      title: meta.label,
      quantity: bookingItem.units,
      price: bookingItem.custom_price
        ? bookingItem.custom_price
        : meta.unit_cost,
      price_unit: meta.unit_cost_basis,
      subtotal: (
        bookingItem.primary_amount as unknown as { [key: string]: number }
      )[hostId],
      taxes_count: taxesArray.length,
      taxes_percent: taxTotalPercent,
      taxes_total: taxTotal,
      host_fee_count: feesArray.length,
      host_fee_total: feeTotal,
      item_modified_date: bookingItem.modified,
      custom_image: !!imageId,
      image_id: imageId,
    };
    addOnsArray.push(itemObj as BookingAddOnItem);
  });

  // visible add-on details
  const visible_add_ons = !!publicAddOns?.length;
  const visibleAddOnsArray: BookingAddOnItem[] = [];
  forEach(publicAddOns, (item: BookingAddOnItem, i: number) => {
    const imageId = get(item, "image_ids[0]", "");
    const itemObj = {
      item_id: item._id,
      category: item.category,
      subcategory: item.subcategory,
      label: item.label,
      unit_cost: item.unit_cost,
      unit_cost_basis: item.unit_cost_basis,
      max_quantity: item.count,
      sort_order: i + 1,
      custom_image: !!imageId,
      image_id: imageId,
    };
    visibleAddOnsArray.push(itemObj as unknown as BookingAddOnItem);
  });

  const {
    after_midnight,
    duration,
    guest_id,
    number_of_guests,
    _id: booking_id,
    is_instant_booking,
    base_rate,
  } = bookingObj;

  const normalizeContractObj: NormalizedContractObject = {
    host_add_ons_base,
    host_add_ons_taxes,
    host_add_ons_fees,
    host_add_ons_subtotal,
    host_add_ons_unique_items_count: itemsArray.length,
    host_add_ons_total_items_count: sum(
      map(itemsArray, function (item: { units: number }) {
        return item.units;
      }),
    ),
    host_add_ons_guest_processing_fee,
    host_add_ons_host_processing_fee,
    host_add_ons_processing_fee: (
      host_add_ons_guest_processing_fee + host_add_ons_host_processing_fee
    ).toFixed(2),
    host_add_ons_host_payout,
    host_add_ons_attached: addOnsArray,
    space_rental_guest_processing_fee,
    space_rental_host_processing_fee,
    space_rental_processing_fee,
    space_rental_host_payout,
    guest_payment_amount,
    guest_payment_processing_fee,
    guest_payment_processing_fee_percent,
    host_payout_amount,
    host_payout_processing_fee,
    host_payout_processing_fee_percent,
    subtotal_amount,
    after_midnight,
    duration,
    guest_id,
    number_of_guests,
    cleaning_fee_set,
    cleaning_fee_amount,
    day_rate_set,
    day_rate_amount,
    day_rate_percent,
    day_count,
    booking_id,
    booking_status,
    is_instant_booking,
    bkg_has_price_by_day,
    bkg_has_attendee_rate,
    attendee_upcharge,
    hourly_rate,
    base_rate,
    visible_add_ons,
    host_add_ons_visible: visibleAddOnsArray,
  };

  if ("accept" === action) {
    normalizeContractObj.host_add_ons_accepted = addOnsArray;
    if (normalizeContractObj.host_add_ons_attached) {
      normalizeContractObj.host_add_ons_attached = [];
    }
  } else if ("decline" === action) {
    normalizeContractObj.host_add_ons_declined = addOnsArray;
    if (normalizeContractObj.host_add_ons_attached) {
      normalizeContractObj.host_add_ons_attached = [];
    }
  } else if (pendingContract) {
    normalizeContractObj.host_add_ons_pending = addOnsArray;
    normalizeContractObj.host_add_ons_attached = [];
  }
  return normalizeContractObj;
};

/** A `YYYY-MM-DD` string: a calendar day, carrying no time and no zone. */
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Formats a schedule date that wasn't given in `{y, m, d}` form as `YYYY-MM-DD`, falling back
 * to today when it can't be read. `YYYY-MM-DD` strings are parsed as a local day, since
 * `new Date` reads them as UTC midnight and would report the day before west of Greenwich.
 */
const formatScheduleDate = (scheduleDate: unknown): string => {
  const parsed =
    typeof scheduleDate === "string" && DATE_ONLY_PATTERN.test(scheduleDate)
      ? parseISO(scheduleDate)
      : new Date(scheduleDate as string | number | Date);

  return format(isValid(parsed) ? parsed : new Date(), "yyyy-MM-dd");
};

export const normalizeBoardObj = (
  project: IProject,
  checkTastemaker: boolean = false,
) => {
  const scheduleDate = get(project.schedule, ["0", "start", "date"], {});
  // `format` reads the local calendar day, where `toISOString` would report UTC and roll
  // the date forward for anyone west of Greenwich in the evening.
  const formattedDate = scheduleDate.m
    ? new Date(
        Number(scheduleDate.y),
        Number(scheduleDate.m) - 1,
        Number(scheduleDate.d),
      )
    : formatScheduleDate(scheduleDate);

  const boardParams = {
    board_id: project._id,
    board_activity: project.activity,
    board_date: formattedDate,
    board_location: project.location,
    board_name: project.name,
    total_listing_count: project.count,
    board_external_id: project.external_id,
  } satisfies NormalizedBoardObject;
  if (checkTastemaker) {
    const isTastemaker = !!project.tastemaker;
    mergeObject(boardParams, {
      is_tastemaker: isTastemaker,
    });
  }
  return boardParams;
};

export const mergeObject = (
  obj: Record<string, unknown>,
  objToAdd: Record<string, unknown>,
) => {
  for (const attrname in objToAdd) {
    if (Object.prototype.hasOwnProperty.call(objToAdd, attrname)) {
      obj[attrname] = objToAdd[attrname];
    }
  }
  return obj;
};

/**
 * Builds a Switch Growth `dedup_key` used to connect server-side and
 * client-side events for GTM. Format: `event_type|identity|event_detail|id`.
 * `identity` is the explicitly passed `userId` when available (server callers
 * should pass it), otherwise the logged-in user id (`SSO_ID_COOKIE`) or the
 * Segment anonymous id (`ajs_anonymous_id`) read from cookies in the browser.
 */
export const buildDedupKey = ({
  eventType,
  eventDetail,
  id,
  userId,
}: {
  eventType: string;
  eventDetail: string;
  id: string | number | undefined;
  userId?: string;
}): string => {
  const identity =
    userId ||
    (getCookie(SSO_ID_COOKIE) as string | undefined) ||
    (getCookie("ajs_anonymous_id") as string | undefined) ||
    "";
  return [eventType, identity, eventDetail, id ?? ""].join("|");
};

/** The payload shared by the AI instant-answers events. */
export const buildAiAnswersProperties = ({
  listingId,
  ssoId,
}: AiAnswersEventProperties) => ({
  sso_id: ssoId || (getCookie(SSO_ID_COOKIE) as string) || "",
  listing_id: listingId,
  timestamp: Date.now(),
  device: typeof navigator === "undefined" ? "" : navigator.userAgent,
  platform: "web",
});
